// src/styles/kuizyTheme.js
import { createContext, useContext, useEffect, useMemo, useState } from "react";

export const BRAND = { primary: "#ff6ba9", secondary: "#49acff" };

export const themePresets = {
  dark: {
    bg: "bg-gray-950",
    bgGrad: "bg-gradient-to-b from-gray-950 via-gray-900 to-gray-950",
    card: "bg-gray-900/60 border border-white/5",
    cardsolid: "bg-gray-900 border border-white/5",
    soft: "bg-white/5",
    softBorder: "border-white/10",
    border: "border-white/5",
    text: "text-white",
    subtext: "text-gray-400",
    subtextStrong: "text-gray-300",
    softText: "text-gray-300",
    neutralBtn: "bg-white/5 text-white",
    inputBg: "bg-gray-800",
    inputText: "text-white",
    inputBorder: "border-white/10",
    track: "bg-white/10",
    menuText: "text-white"
  },
  white: {
    bg: "bg-white",
    bgGrad: "bg-gradient-to-b from-white via-gray-50 to-white",
    card: "bg-white/60 border border-gray-200",
    cardsolid: "bg-white border border-gray-200",
    soft: "bg-gray-100",
    softBorder: "border-gray-200",
    border: "border-gray-200",
    text: "text-gray-900",
    subtext: "text-gray-500",
    subtextStrong: "text-gray-700",
    softText: "text-gray-700",
    neutralBtn: "bg-gray-100 text-gray-900",
    inputBg: "bg-gray-100",
    inputText: "text-gray-900",
    inputBorder: "border-gray-300",
    track: "bg-gray-200",
    menuText: "text-gray-900"
  },
  primary: {
    bg: "bg-gradient-to-b from-pink-50 via-white to-pink-50",
    bgGrad: "bg-gradient-to-b from-pink-50 via-white to-pink-50",
    card: "bg-white/60 border border-pink-100",
    cardsolid: "bg-white border border-pink-100",
    soft: "bg-pink-50",
    softBorder: "border-pink-200",
    border: "border-pink-200",
    text: "text-gray-900",
    subtext: "text-pink-700",
    subtextStrong: "text-gray-700",
    softText: "text-pink-800",
    neutralBtn: "bg-pink-50 text-gray-900",
    inputBg: "bg-pink-50",
    inputText: "text-gray-900",
    inputBorder: "border-pink-200",
    track: "bg-pink-200",
    menuText: "text-gray-900"
  },
  secondary: {
    bg: "bg-gradient-to-b from-blue-50 via-white to-blue-50",
    bgGrad: "bg-gradient-to-b from-blue-50 via-white to-blue-50",
    card: "bg-white/60 border border-blue-100",
    cardsolid: "bg-white border border-blue-100",
    soft: "bg-blue-50",
    softBorder: "border-blue-200",
    border: "border-blue-200",
    text: "text-gray-900",
    subtext: "text-blue-700",
    subtextStrong: "text-gray-700",
    softText: "text-blue-800",
    neutralBtn: "bg-blue-50 text-gray-900",
    inputBg: "bg-blue-50",
    inputText: "text-gray-900",
    inputBorder: "border-blue-200",
    track: "bg-blue-200",
    menuText: "text-gray-900"
  }
};

const composeTokens = (key) => {
  const p = themePresets[key] || themePresets.dark;
  return {
    key,
    bg: p.bgGrad || p.bg,
    bgBase: p.bg,
    bgGrad: p.bgGrad,
    card: p.card,
    cardsolid: p.cardsolid,
    text: p.text,
    subtext: p.subtext,
    subtextStrong: p.subtextStrong,
    soft: `${p.soft} ${p.softBorder}`,
    softOnly: p.soft,
    softBorder: p.softBorder,
    border: p.border,
    softText: p.softText,
    neutralBtn: p.neutralBtn,
    input: `${p.inputBg} ${p.inputBorder} ${p.inputText}`,
    inputBg: p.inputBg,
    inputText: p.inputText,
    inputBorder: p.inputBorder,
    track: p.track,
    chip: `${p.soft} ${p.border} ${p.text}`,
    menuText: p.menuText,
    btnPrimary: "bg-primary text-white",
    btnSecondary: "bg-secondary text-white",
    btn: key === "secondary" ? "bg-secondary text-white" : "bg-primary text-white"
  };
};

export const THEME_STORAGE_KEY = "theme";
export const getThemeKey = () => localStorage.getItem(THEME_STORAGE_KEY) || "dark";
export const setThemeKey = (k) => { try { localStorage.setItem(THEME_STORAGE_KEY, k); } catch { } };
export const getThemeTokens = (k) => composeTokens(k || getThemeKey());

const ThemeContext = createContext({
  theme: "white",
  setTheme: () => { },
  t: composeTokens("white"),
  presets: themePresets
});

export function KuizyThemeProvider({ children }) {
  const [theme, setTheme] = useState(getThemeKey());
  useEffect(() => { setThemeKey(theme); }, [theme]);
  const t = useMemo(() => getThemeTokens(theme), [theme]);
  const value = useMemo(() => ({ theme, setTheme, t, presets: themePresets }), [theme, t]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useKuizyTheme() {
  return useContext(ThemeContext);
}
